import React, { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const Y1="#fce38a", Y2="#fbd758", Y3="#fdefbc", BK="#111111", DG="#2a2a2a", MG="#888888", LG="#f5f5f3", WH="#ffffff";

function useResponsive(){
  const[w,setW]=useState(typeof window!=="undefined"?window.innerWidth:1200);
  useEffect(()=>{const h=()=>setW(window.innerWidth);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  return{isMobile:w<700,isTablet:w<1024,w};
}

function useReveal(threshold=0.1){
  const ref=useRef(null);const[vis,setVis]=useState(false);
  useEffect(()=>{const obs=new IntersectionObserver(([e])=>{if(e.isIntersecting)setVis(true)},{threshold});if(ref.current)obs.observe(ref.current);return()=>obs.disconnect();},[]);
  return[ref,vis];
}

function AnimNum({target,suffix="",dur=1200}){
  const[val,setVal]=useState(0);const[ref,vis]=useReveal(0.3);
  useEffect(()=>{if(!vis)return;const start=Date.now();const isF=String(target).includes(".");const tick=()=>{const p=Math.min((Date.now()-start)/dur,1);const e=1-Math.pow(1-p,3);setVal(isF?(e*target).toFixed(2):Math.round(e*target));if(p<1)requestAnimationFrame(tick);};requestAnimationFrame(tick);},[vis,target,dur]);
  return (<span ref={ref}>{val}{suffix}</span>);
}

function Section({children,bg=WH,id,pt="72px",pb="72px"}){
  const[ref,vis]=useReveal();
  return(
    <section id={id} ref={ref} style={{background:bg,paddingTop:pt,paddingBottom:pb,opacity:vis?1:0,transform:vis?"none":"translateY(20px)",transition:"opacity 0.55s ease,transform 0.55s ease"}}>
      <div style={{maxWidth:1100,margin:"0 auto",padding:"0 24px"}}>{children}</div>
    </section>
  );
}

function Chip({label}){
  return(<span style={{display:"inline-block",background:"transparent",color:MG,fontFamily:"'Lato',sans-serif",fontWeight:700,fontSize:10,letterSpacing:2.5,textTransform:"uppercase",borderBottom:`2px solid ${Y2}`,paddingBottom:2}}>{label}</span>);
}

function H2({children,size=36}){
  const{isMobile}=useResponsive();
  return(<h2 style={{fontFamily:"Georgia,serif",fontSize:isMobile?Math.round(size*0.75):size,fontWeight:700,color:BK,lineHeight:1.2,margin:"14px 0 10px"}}>{children}</h2>);
}

function Body({children,size=16,color}){
  const{isMobile}=useResponsive();
  return(<p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:size,color:color||DG,lineHeight:1.82,margin:"0 0 16px"}}>{children}</p>);
}

function CTip({active,payload,label}){
  if(!active||!payload?.length)return null;
  return(
    <div style={{background:WH,border:"1px solid #e0e0e0",padding:"10px 14px",borderRadius:4,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
      <p style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:12,margin:"0 0 6px",color:BK}}>{label}</p>
      {payload.map((p,i)=><p key={i} style={{fontFamily:"Lato,sans-serif",fontSize:12,color:p.color||DG,margin:"2px 0"}}>{p.name}: {p.value}</p>)}
    </div>
  );
}

function EmailModal({onClose}){
  const[email,setEmail]=useState("");
  const[name,setName]=useState("");
  const[org,setOrg]=useState("");
  const[submitted,setSubmitted]=useState(false);
  const[err,setErr]=useState("");
  const{isMobile}=useResponsive();

  const validate=(e)=>{
    e.preventDefault();
    if(!email||!email.includes("@")){setErr("Please enter a valid email address.");return;}
    setErr("");setSubmitted(true);
  };

  const inp={width:"100%",boxSizing:"border-box",fontFamily:"Lato,sans-serif",fontSize:13,color:BK,background:LG,border:"1px solid #ddd",borderRadius:3,padding:"11px 13px",marginBottom:11,outline:"none"};

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:WH,borderRadius:6,padding:isMobile?"28px 22px":"44px 48px",maxWidth:460,width:"100%",position:"relative",boxShadow:"0 24px 64px rgba(0,0,0,0.22)"}}>
        <button onClick={onClose} style={{position:"absolute",top:14,right:16,background:"none",border:"none",cursor:"pointer",fontFamily:"Lato,sans-serif",fontSize:11,color:MG}}>✕</button>
        {!submitted?(
          <>
            <div style={{width:36,height:3,background:Y2,marginBottom:18}}/>
            <h3 style={{fontFamily:"Georgia,serif",fontSize:21,color:BK,margin:"0 0 8px",lineHeight:1.3}}>Get the Full 2025 Outcomes Report</h3>
            <p style={{fontFamily:"Georgia,serif",fontSize:14,color:MG,lineHeight:1.7,margin:"0 0 22px"}}>Enter your details and we'll send you a copy along with future FareRx research.</p>
            <form onSubmit={validate}>
              <input style={inp} type="text" placeholder="Your name" value={name} onChange={e=>setName(e.target.value)}/>
              <input style={inp} type="email" placeholder="Work email *" value={email} onChange={e=>setEmail(e.target.value)} required/>
              <input style={inp} type="text" placeholder="Organization (optional)" value={org} onChange={e=>setOrg(e.target.value)}/>
              {err&&<p style={{fontFamily:"Lato,sans-serif",fontSize:11,color:"#c0392b",margin:"-5px 0 10px"}}>{err}</p>}
              <button type="submit" style={{width:"100%",background:BK,color:Y1,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:11,letterSpacing:1.5,padding:"14px",borderRadius:3,border:"none",cursor:"pointer",textTransform:"uppercase",marginTop:4}}>
                Download Whitepaper →
              </button>
              <p style={{fontFamily:"Lato,sans-serif",fontSize:10,color:MG,marginTop:10,textAlign:"center"}}>No spam. Unsubscribe anytime.</p>
            </form>
          </>
        ):(
          <div style={{textAlign:"center",padding:"12px 0"}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:Y3,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:20,color:BK}}>✓</div>
            <h3 style={{fontFamily:"Georgia,serif",fontSize:22,color:BK,margin:"0 0 8px"}}>You're all set.</h3>
            <p style={{fontFamily:"Georgia,serif",fontSize:14,color:MG,lineHeight:1.7,margin:"0 0 22px"}}>Thank you for your interest in FareRx. Your download is ready below.</p>
            <a href="/FareRx_Whitepaper_2025_Final.pdf" download="FareRx_Whitepaper_2025_Final.pdf"
              style={{display:"inline-block",background:BK,color:Y1,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:11,letterSpacing:1.5,padding:"13px 28px",borderRadius:3,textDecoration:"none",textTransform:"uppercase",marginBottom:14}}>
              ↓ Download PDF
            </a>
            <br/>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"Lato,sans-serif",fontSize:11,color:MG,letterSpacing:1,marginTop:6}}>Continue reading ↓</button>
          </div>
        )}
      </div>
    </div>
  );
}

const pre4=[
  {metric:"Self-Rated Health",pre:3.71,post:3.84},{metric:"Meal Confidence",pre:4.08,post:4.20},
  {metric:"Grocery Confidence",pre:4.10,post:4.41},{metric:"Veg Servings",pre:4.11,post:4.26},
  {metric:"Med Adherence",pre:3.73,post:3.79},
];
const pre12=[
  {metric:"Self-Rated Health",pre:3.70,post:3.61},{metric:"Meal Confidence",pre:3.65,post:4.09},
  {metric:"Grocery Confidence",pre:3.91,post:4.26},{metric:"Veg Servings",pre:4.88,post:4.50},
  {metric:"Med Adherence",pre:3.61,post:3.83},
];
const perceptions=[
  {label:"Easier meal prep",oct:79,dec:80,combined:80},
  {label:"Less food stress",oct:54,dec:75,combined:65},
  {label:"Connected to plan",oct:67,dec:82,combined:75},
  {label:"Likely to stay",oct:59,dec:84,combined:72},
  {label:"New foods",oct:61,dec:61,combined:61},
];
const doseData=[
  {metric:"Meal Confidence",wk4:0.13,wk12:0.44},
  {metric:"Grocery Confidence",wk4:0.30,wk12:0.35},
  {metric:"Med Adherence",wk4:0.06,wk12:0.22},
];
const deliveryData=[
  {metric:"On-Time",oct:96,dec:100,combined:98},
  {metric:"Fresh Produce",oct:96,dec:100,combined:98},
  {metric:"Driver Quality",oct:100,dec:98,combined:99},
  {metric:"Items Matched",oct:79,dec:69,combined:74},
];
const execSummaryRows=[
  {finding:"72–84% of members said this benefit makes them more likely to stay with their health plan",means:"Members directly attribute plan loyalty to this grocery benefit.",matters:"In a competitive MA market, member retention has a measurable dollar value."},
  {finding:"89–92% of members maintained or improved medication adherence over the program period",means:"The benefit supports not just nutrition, but compliance behaviors that drive total cost of care.",matters:"Non-adherence costs the US healthcare system approximately $300B per year."},
  {finding:"Members in the 12-week program showed 3.7× greater improvement in medication adherence than 4-week members",means:"Longer programs compound the effect. Duration is a clinical design variable, not a budget one.",matters:"Plans can calibrate duration to population risk level and expected ROI threshold."},
  {finding:"Members reported significant gains in grocery shopping and meal planning confidence by program end",means:"These are validated behavioral health indicators correlated with consistent healthy food choices.",matters:"Behavioral change is a leading indicator for A1c control, ER avoidance, and preventable readmission reduction."},
];
const litRows=[
  {metric:"A1c reduction (MTM, food-insecure DM)",lit:"0.3–2.0% (Berkowitz 2019)[3]",farerx:"Clinical data pending*"},
  {metric:"Medication adherence maintenance",lit:"Improved in MTM arms[6]",farerx:"89–92% maintained or improved"},
  {metric:"Healthcare cost savings (modeled)",lit:"Net-saving in 49/50 states[5]",farerx:"Outcomes framework in progress"},
  {metric:"Member retention signal",lit:"Not broadly studied",farerx:"72–84% more likely to stay"},
];
const zipRows=[
  {zip:"19132",nbhd:"North Philadelphia",tier:"high"},{zip:"19139",nbhd:"West Philadelphia",tier:"high"},
  {zip:"19142",nbhd:"SW Philadelphia",tier:"high"},{zip:"19143",nbhd:"SW Philadelphia",tier:"high"},
  {zip:"19134",nbhd:"Port Richmond",tier:"high"},{zip:"19144",nbhd:"Germantown",tier:"high"},
  {zip:"19124",nbhd:"Frankford",tier:"high"},{zip:"19150",nbhd:"Mt. Airy",tier:"moderate"},
  {zip:"19148",nbhd:"South Philadelphia",tier:"moderate"},{zip:"19126",nbhd:"West Oak Lane",tier:"moderate"},
  {zip:"19116",nbhd:"NE Philadelphia",tier:"low"},{zip:"19111",nbhd:"NE Philadelphia",tier:"low"},
  {zip:"19136",nbhd:"NE Philadelphia",tier:"low"},{zip:"19061",nbhd:"Delaware County",tier:"low"},
  {zip:"19064",nbhd:"Springfield, DE Co.",tier:"low"},{zip:"19078",nbhd:"Ridley Park",tier:"low"},
  {zip:"19050",nbhd:"Lansdowne",tier:"low"},{zip:"19026",nbhd:"Drexel Hill",tier:"low"},
  {zip:"19036",nbhd:"Glenolden",tier:"low"},{zip:"19015",nbhd:"Delaware County",tier:"low"},
  {zip:"19020",nbhd:"Bensalem",tier:"low"},{zip:"19002",nbhd:"Ambler",tier:"low"},
  {zip:"19067",nbhd:"Morrisville",tier:"low"},{zip:"19525",nbhd:"Gilbertsville",tier:"low"},
];
const refs=[
  {n:1,text:'Mozaffarian D et al. "Food Is Medicine" strategies for nutrition security and cardiometabolic health equity. J Am Coll Cardiol. 2024;83(8):843–864.',url:"https://www.jacc.org/doi/10.1016/j.jacc.2023.11.009"},
  {n:2,text:"Downer S, Berkowitz SA, Harlan TS, Olstad DL, Mozaffarian D. Food is medicine: actions to integrate food and nutrition into healthcare. BMJ. 2020;369:m2482.",url:"https://www.bmj.com/content/369/bmj.m2482"},
  {n:3,text:"Berkowitz SA et al. Medically tailored meal delivery for diabetes patients with food insecurity: a randomized cross-over trial. J Gen Intern Med. 2019;34(3):396–404.",url:"https://link.springer.com/article/10.1007/s11606-018-4716-z"},
  {n:4,text:"Doyle J, Alsan M, Skelley N, Lu Y, Cawley J. Effect of an intensive food-as-medicine program on health and health care use: a randomized clinical trial. JAMA Intern Med. 2024;184:154–163.",url:"https://jamanetwork.com/journals/jamainternalmedicine/fullarticle/2812566"},
  {n:5,text:"Deng S, Hager K, Wang L et al. Estimated impact of medically tailored meals on health care use and expenditures in 50 US states. Health Affairs. 2025;44(4):433–442.",url:"https://www.healthaffairs.org/doi/10.1377/hlthaff.2024.00899"},
  {n:6,text:"Go AS et al. Effect of medically tailored meals on clinical outcomes in recently hospitalized high-risk adults. Med Care. 2022;60(10):750–758.",url:"https://journals.lww.com/lww-medicalcare/abstract/2022/10000/effect_of_medically_tailored_meals_on_clinical.5.aspx"},
  {n:7,text:"Nguyen HQ et al. Association of a Medicare Advantage posthospitalization home meal delivery benefit with rehospitalization and death. JAMA Health Forum. 2023;4(6):e231678.",url:"https://jamanetwork.com/journals/jama-health-forum/fullarticle/2806106"},
  {n:8,text:"Chen AMH et al. Food as medicine? Exploring the impact of providing healthy foods on adherence and clinical and economic outcomes. Explor Res Clin Soc Pharm. 2022;5:100129.",url:"https://www.sciencedirect.com/science/article/pii/S2667276622000476"},
];

function Nav({onDownload}){
  const[scrolled,setScrolled]=useState(false);
  const[open,setOpen]=useState(false);
  const{isMobile}=useResponsive();
  useEffect(()=>{const h=()=>setScrolled(window.scrollY>60);window.addEventListener("scroll",h);return()=>window.removeEventListener("scroll",h);},[]);
  const go=(id)=>{document.getElementById(id)?.scrollIntoView({behavior:"smooth"});setOpen(false);};
  const items=[["summary","Summary"],["problem","Problem"],["results","Results"],["reach","Reach"],["retention","Retention"],["voices","Members"],["refs","Literature"]];
  return(
    <nav style={{position:"fixed",top:0,left:0,right:0,zIndex:100,background:scrolled?"rgba(17,17,17,0.96)":"transparent",backdropFilter:scrolled?"blur(12px)":"none",padding:isMobile?"12px 20px":"10px 36px",display:"flex",alignItems:"center",justifyContent:"space-between",transition:"background 0.3s ease",borderBottom:scrolled?"1px solid rgba(255,255,255,0.06)":"none"}}>
      <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:14,color:Y1,letterSpacing:2.5}}>FARERX</div>
      {isMobile?(
        <>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={onDownload} style={{background:Y2,border:"none",cursor:"pointer",color:BK,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:9,padding:"7px 11px",borderRadius:2,letterSpacing:1}}>↓ PDF</button>
            <button onClick={()=>setOpen(!open)} style={{background:"none",border:"1px solid rgba(255,255,255,0.2)",cursor:"pointer",color:WH,fontFamily:"Lato,sans-serif",fontSize:10,padding:"6px 11px",borderRadius:2,letterSpacing:1}}>{open?"✕":"MENU"}</button>
          </div>
          {open&&(
            <div style={{position:"absolute",top:"100%",left:0,right:0,background:"rgba(15,15,15,0.99)",padding:"6px 0",borderTop:"1px solid rgba(255,255,255,0.07)"}}>
              {items.map(([id,label])=>(
                <button key={id} onClick={()=>go(id)} style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",borderBottom:"1px solid rgba(255,255,255,0.05)",cursor:"pointer",fontFamily:"Lato,sans-serif",fontSize:13,fontWeight:700,color:WH,letterSpacing:1,padding:"14px 20px",textTransform:"uppercase"}}>{label}</button>
              ))}
            </div>
          )}
        </>
      ):(
        <div style={{display:"flex",gap:20,alignItems:"center"}}>
          {items.map(([id,label])=>(
            <button key={id} onClick={()=>go(id)} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"Lato,sans-serif",fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.65)",letterSpacing:1.2,textTransform:"uppercase",padding:"2px 0"}}>{label}</button>
          ))}
          <button onClick={onDownload} style={{background:Y2,border:"none",cursor:"pointer",color:BK,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,padding:"8px 16px",borderRadius:2,letterSpacing:1,textTransform:"uppercase",marginLeft:10}}>↓ Download PDF</button>
        </div>
      )}
    </nav>
  );
}

function Hero({onDownload}){
  const{isMobile,isTablet}=useResponsive();
  return(
    <div style={{background:"#0f0f0f",minHeight:"100vh",display:"flex",flexDirection:"column",justifyContent:"center",position:"relative",overflow:"hidden",padding:isMobile?"100px 24px 64px":"130px 0 90px"}}>
      <div style={{position:"absolute",inset:0,backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 47px,rgba(255,255,255,0.022) 47px,rgba(255,255,255,0.022) 48px)",zIndex:0,pointerEvents:"none"}}/>
      <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:Y2,zIndex:1}}/>
      <div style={{maxWidth:1100,margin:"0 auto",width:"100%",position:"relative",zIndex:2,padding:"0 36px"}}>
        <div style={{display:"grid",gridTemplateColumns:isTablet?"1fr":"5fr 4fr",gap:isTablet?0:80,alignItems:"center"}}>
          <div>
            <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:20,height:2,background:Y2}}/>
              <span style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,letterSpacing:3,color:"rgba(255,255,255,0.38)",textTransform:"uppercase"}}>Food is Medicine · 2025 Outcomes Report</span>
            </div>
            <h1 style={{fontFamily:"Georgia,serif",fontSize:isMobile?30:isTablet?40:52,fontWeight:700,color:WH,lineHeight:1.12,margin:"0 0 10px"}}>
              Medically Tailored<br/>
              <span style={{color:Y1}}>Groceries</span> as a<br/>
              Supplemental Benefit
            </h1>
            <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:17,color:"rgba(255,255,255,0.48)",lineHeight:1.78,margin:"18px 0 28px",maxWidth:480}}>
              Pre/post outcomes from 102 matched member pairs across four SSBCI program cohorts. September–December 2025.
            </p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:32}}>
              {["n=102 matched pairs","SSBCI Benefit","Diabetes · Depression · Chronic Illness"].map(t=>(
                <div key={t} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:2,padding:"5px 10px"}}>
                  <span style={{fontFamily:"Lato,sans-serif",fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:0.5}}>{t}</span>
                </div>
              ))}
            </div>
            <button onClick={onDownload} style={{background:Y2,color:BK,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:11,letterSpacing:1.5,padding:"13px 26px",borderRadius:2,border:"none",cursor:"pointer",textTransform:"uppercase"}}>↓ Download Full Report</button>
            <div style={{marginTop:32,paddingTop:22,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
              <div style={{fontFamily:"Lato,sans-serif",fontSize:9,color:"rgba(255,255,255,0.22)",letterSpacing:2,textTransform:"uppercase",marginBottom:4}}>Published by</div>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:12,color:"rgba(255,255,255,0.45)",letterSpacing:0.3}}>FareRx Member Hospitality Group · Philadelphia, PA · farerx.com</div>
            </div>
          </div>
          {!isTablet&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[
                {n:84,s:"%",l:"of Dec cohort more likely to stay with their plan"},
                {n:102,s:"",l:"matched pre/post survey pairs"},
                {n:4.90,s:"/5",l:"average delivery satisfaction rating"},
                {n:91,s:"%",l:"of 12-week members maintained or improved med adherence"},
              ].map((s,i)=>(
                <div key={i} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:4,padding:"22px 16px"}}>
                  <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:38,color:i%2===0?Y1:WH,lineHeight:1}}>
                    <AnimNum target={parseFloat(s.n)} suffix={s.s}/>
                  </div>
                  <div style={{fontFamily:"Georgia,serif",fontSize:12,color:"rgba(255,255,255,0.36)",marginTop:8,lineHeight:1.55}}>{s.l}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {isTablet&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:36}}>
            {[
              {n:84,s:"%",l:"Likely to stay with plan"},
              {n:4.90,s:"/5",l:"Delivery rating"},
              {n:91,s:"%",l:"Med adherence maintained"},
              {n:102,s:"",l:"Matched survey pairs"},
            ].map((s,i)=>(
              <div key={i} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:3,padding:"14px 12px"}}>
                <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:28,color:Y1,lineHeight:1}}>
                  <AnimNum target={parseFloat(s.n)} suffix={s.s}/>
                </div>
                <div style={{fontFamily:"Georgia,serif",fontSize:11,color:"rgba(255,255,255,0.38)",marginTop:5,lineHeight:1.4}}>{s.l}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function App(){
  const{isMobile,isTablet}=useResponsive();
  const[showModal,setShowModal]=useState(false);
  const col2=isTablet?"1fr":"1fr 1fr";
  const col3=isMobile?"1fr 1fr":"repeat(3,1fr)";

  useEffect(()=>{
    const l=document.createElement("link");
    l.href="https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap";
    l.rel="stylesheet";document.head.appendChild(l);
  },[]);

  const thStyle={fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,letterSpacing:1,textTransform:"uppercase",padding:"10px 14px",textAlign:"left",color:MG,background:LG,borderBottom:`2px solid ${Y2}`};
  const tdStyle={fontFamily:"Georgia,serif",fontSize:13,color:DG,padding:"12px 14px",lineHeight:1.6,borderBottom:"1px solid #ebebeb",verticalAlign:"top"};
  const tdY={...tdStyle,color:BK,fontWeight:"bold"};

  return(
    <div style={{fontFamily:"Georgia,serif",color:BK,background:WH}}>
      {showModal&&<EmailModal onClose={()=>setShowModal(false)}/>}
      <Nav onDownload={()=>setShowModal(true)}/>
      <Hero onDownload={()=>setShowModal(true)}/>

      {/* EXEC SUMMARY */}
      <Section id="summary" bg={LG}>
        <div style={{marginBottom:32}}>
          <Chip label="Executive Summary"/>
          <H2>What the data shows.</H2>
          <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?13:15,color:MG,margin:"4px 0 0",maxWidth:540,lineHeight:1.7}}>Four key findings from 102 matched member pairs across four SSBCI cohorts. September–December 2025.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:10,marginBottom:28}}>
          {execSummaryRows.map((row,i)=>(
            <div key={i} style={{background:WH,borderRadius:4,padding:isMobile?"18px 16px":"24px 22px",borderLeft:`3px solid ${Y2}`}}>
              <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:15,color:BK,lineHeight:1.6,fontWeight:700,margin:"0 0 12px",paddingBottom:10,borderBottom:"1px solid #eee"}}>{row.finding}</p>
              <div style={{marginBottom:8}}>
                <div style={{fontFamily:"Lato,sans-serif",fontSize:9,fontWeight:700,letterSpacing:1.5,color:MG,textTransform:"uppercase",marginBottom:3}}>What it means</div>
                <p style={{fontFamily:"Georgia,serif",fontSize:13,color:DG,margin:0,lineHeight:1.6}}>{row.means}</p>
              </div>
              <div style={{marginTop:8}}>
                <div style={{fontFamily:"Lato,sans-serif",fontSize:9,fontWeight:700,letterSpacing:1.5,color:MG,textTransform:"uppercase",marginBottom:3}}>Why it matters</div>
                <p style={{fontFamily:"Georgia,serif",fontSize:13,color:DG,margin:0,lineHeight:1.6}}>{row.matters}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10}}>
          {[
            {n:"84%",l:"Dec cohort members more likely to stay with their plan"},
            {n:"3.7×",l:"Medication adherence multiplier — 4-week vs 12-week"},
            {n:"4.90/5",l:"Average member delivery satisfaction rating"},
          ].map((s,i)=>(
            <div key={i} style={{borderTop:`2px solid ${Y2}`,paddingTop:14}}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:isMobile?32:38,color:BK,lineHeight:1}}>{s.n}</div>
              <div style={{fontFamily:"Georgia,serif",fontSize:12,color:MG,marginTop:7,lineHeight:1.5}}>{s.l}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* PROBLEM */}
      <Section id="problem" bg={WH}>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:isTablet?28:80,alignItems:"start"}}>
          <div>
            <Chip label="The Problem"/>
            <H2>Food insecurity is a financial risk — not just a social one.</H2>
          </div>
          <div>
            <Body>More than 44 million Americans experience food insecurity. For health plans managing Medicare Advantage populations with diabetes, depression, and chronic illness, this is a total cost of care problem. Food-insecure members with diabetes have substantially higher rates of ER utilization, medication non-adherence, and preventable readmissions.<sup style={{fontSize:10}}>[1,2]</sup></Body>
            <Body>CMS recognized this in 2018, introducing <strong>Special Supplemental Benefits for Chronically Ill (SSBCI)</strong> — a direct policy pathway for plans to fund food and nutrition services with measurable clinical rationale. All FareRx programs described here were deployed as SSBCI benefits.</Body>
            <div style={{display:"grid",gridTemplateColumns:col3,gap:10,marginTop:20}}>
              {[
                {stat:"$20K+",label:"Avg annual spend for MA member with diabetes + depression"},
                {stat:"44M+",label:"Americans experiencing food insecurity today"},
                {stat:"49 states",label:"Where MTM coverage is modeled as net cost-saving (Health Affairs, 2025)"},
              ].map((s,i)=>(
                <div key={i} style={{borderTop:`2px solid ${Y2}`,paddingTop:12}}>
                  <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:isMobile?20:24,color:BK}}>{s.stat}</div>
                  <div style={{fontFamily:"Georgia,serif",fontSize:12,color:MG,marginTop:5,lineHeight:1.5}}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* PROGRAM DESIGN */}
      <Section bg={LG}>
        <div style={{marginBottom:28,textAlign:isMobile?"left":"center"}}>
          <Chip label="Program Design"/>
          <H2>Two high-need cohorts. One consistent signal.</H2>
          <p style={{fontFamily:"Lato,sans-serif",fontSize:13,color:MG,maxWidth:480,margin:isMobile?"8px 0 0":"8px auto 0",lineHeight:1.6}}>Sept–Dec 2025 · 102 matched pre/post pairs · Philadelphia region · SSBCI benefit design</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:10}}>
          {[
            {title:"4-Week Program",sub:"Oct–Dec 2025 cohorts · n=79 matched pairs",
             desc:"Members diagnosed with diabetes and depression enrolled through a health plan SSBCI benefit. Four weekly deliveries of medically tailored groceries curated by FareRx dietitians for glycemic management and nutritional quality.",
             tags:["Diabetes","Depression","SSBCI"],accent:Y2},
            {title:"12-Week Extended Care",sub:"Sept–Dec 2025 · n=23 matched pairs",
             desc:"Low-Income Subsidy (LIS)-eligible members with at least one qualifying chronic illness. Twelve consecutive weekly deliveries. Designed to measure whether sustained nutritional support compounds behavioral change beyond the 4-week baseline.",
             tags:["LIS-Eligible","Chronic Illness","12 Weeks","SSBCI"],accent:BK},
          ].map((c,i)=>(
            <div key={i} style={{background:WH,borderRadius:4,padding:isMobile?"20px 16px":"28px 26px",borderTop:`3px solid ${c.accent}`}}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:17,color:BK,marginBottom:3}}>{c.title}</div>
              <div style={{fontFamily:"Lato,sans-serif",fontSize:11,color:MG,marginBottom:12}}>{c.sub}</div>
              <p style={{fontFamily:"Georgia,serif",fontSize:14,color:DG,lineHeight:1.72,margin:"0 0 16px"}}>{c.desc}</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {c.tags.map(t=>(<span key={t} style={{background:LG,color:DG,fontFamily:"Lato,sans-serif",fontSize:9,fontWeight:700,letterSpacing:1.5,padding:"4px 8px",borderRadius:2,textTransform:"uppercase"}}>{t}</span>))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* RESULTS */}
      <Section id="results" bg={WH}>
        <Chip label="Program Outcomes"/>
        <H2>Pre/Post Results by Program</H2>
        <p style={{fontFamily:"Georgia,serif",fontSize:14,color:MG,marginBottom:36,maxWidth:540,lineHeight:1.7}}>Mean scores before and after program completion. Matched pairs only. 5-point scale.</p>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:isMobile?40:52}}>
          {[
            {label:"4-Week Program (n=79) · Diabetes + Depression",data:pre4,postColor:Y2,note:null},
            {label:"12-Week Extended Care (n=23) · LIS-Eligible",data:pre12,postColor:"#444",
             note:"Note on negative deltas: The slight self-rated health decline reflects higher-acuity members at baseline. The vegetable servings decline reflects a ceiling effect — a pre-score of 4.88/5 leaves minimal room for measured improvement."},
          ].map((prog,pi)=>(
            <div key={pi}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <div style={{width:8,height:8,background:prog.postColor,borderRadius:"50%",flexShrink:0}}/>
                <h3 style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:12,margin:0,color:BK,lineHeight:1.4}}>{prog.label}</h3>
              </div>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={prog.data} layout="vertical" margin={{left:4,right:12,top:0,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" horizontal={false}/>
                  <XAxis type="number" domain={[3,5]} tick={{fontFamily:"Lato,sans-serif",fontSize:10,fill:MG}}/>
                  <YAxis type="category" dataKey="metric" width={108} tick={{fontFamily:"Lato,sans-serif",fontSize:isMobile?9:11,fill:DG}}/>
                  <Tooltip content={<CTip/>}/>
                  <Bar dataKey="pre" name="Before" fill="#ddd" radius={[0,2,2,0]} barSize={9}/>
                  <Bar dataKey="post" name="After" fill={prog.postColor} radius={[0,2,2,0]} barSize={9}/>
                </BarChart>
              </ResponsiveContainer>
              <div style={{display:"flex",gap:14,marginTop:6}}>
                {[["#ddd","Before"],[prog.postColor,"After"]].map(([c,l])=>(
                  <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                    <div style={{width:8,height:8,background:c,borderRadius:2}}/>
                    <span style={{fontFamily:"Lato,sans-serif",fontSize:11,color:MG}}>{l}</span>
                  </div>
                ))}
              </div>
              {prog.note&&<p style={{fontFamily:"Lato,sans-serif",fontSize:11,color:MG,marginTop:10,lineHeight:1.65,borderLeft:`2px solid ${Y2}`,paddingLeft:10}}>{prog.note}</p>}
            </div>
          ))}
        </div>

        <div style={{background:LG,borderRadius:4,padding:isMobile?"20px 16px":"26px 32px",marginTop:40,display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:isMobile?20:28,borderTop:`2px solid ${Y2}`}}>
          {[
            {n:"+0.44",l:"Meal confidence gain in 12-week program — largest single-metric improvement"},
            {n:"+0.31",l:"Grocery confidence gain in 4-week program — FareRx's most direct behavioral outcome"},
            {n:"92%",l:"of 4-week members maintained or improved medication adherence"},
          ].map((s,i)=>(
            <div key={i}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:isMobile?30:38,color:BK,lineHeight:1}}>{s.n}</div>
              <div style={{fontFamily:"Georgia,serif",fontSize:12,color:MG,lineHeight:1.5,marginTop:7}}>{s.l}</div>
            </div>
          ))}
        </div>

        <div style={{marginTop:52}}>
          <Chip label="Dose-Response Signal"/>
          <H2>Duration amplifies impact. Up to 3.7×.</H2>
          <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:15,color:MG,marginBottom:26,maxWidth:580,lineHeight:1.8}}>
            The 12-week program produced 3.3–3.7× larger gains than the 4-week program across the most consequential metrics. Program duration is not a budget variable — it's a clinical design variable.<sup style={{fontSize:10}}>[4,6]</sup>
          </p>
          <div style={{display:"grid",gridTemplateColumns:isTablet?"1fr":"190px 1fr",gap:24,alignItems:"center"}}>
            <div style={{borderTop:`2px solid ${Y2}`,paddingTop:14}}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,color:MG,marginBottom:4,textTransform:"uppercase",letterSpacing:1}}>Med Adherence Multiplier</div>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:52,color:BK,lineHeight:1}}>3.7×</div>
              <div style={{fontFamily:"Georgia,serif",fontSize:13,color:MG,marginTop:8,lineHeight:1.5}}>12-week gain (+0.22) vs 4-week gain (+0.06)</div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={doseData} margin={{left:0,right:12,top:8,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false}/>
                <XAxis dataKey="metric" tick={{fontFamily:"Lato,sans-serif",fontSize:isMobile?9:11,fill:DG}}/>
                <YAxis tick={{fontFamily:"Lato,sans-serif",fontSize:10,fill:MG}} tickFormatter={v=>`+${v}`}/>
                <Tooltip content={<CTip/>}/>
                <Bar dataKey="wk4" name="4-Week Gain" fill="#ddd" radius={[3,3,0,0]} barSize={24}/>
                <Bar dataKey="wk12" name="12-Week Gain" fill={Y2} radius={[3,3,0,0]} barSize={24}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Section>

      {/* GEOGRAPHIC REACH */}
      <Section id="reach" bg={BK}>
        <div style={{marginBottom:32}}>
          <Chip label="Geographic Reach"/>
          <h2 style={{fontFamily:"Georgia,serif",fontSize:isMobile?24:32,fontWeight:700,color:WH,lineHeight:1.2,margin:"14px 0 8px"}}>Serving the communities that need it most.</h2>
          <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?13:15,color:"rgba(255,255,255,0.38)",maxWidth:520,margin:0,lineHeight:1.7}}>12-Week Extended Care members spanned 24 zip codes across the Philadelphia region.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:col3,gap:10,marginBottom:24}}>
          {[
            {n:7,label:"Philadelphia zip codes with high food access barriers (USDA FARA)",col:Y1},
            {n:3,label:"Philadelphia zip codes with moderate food access challenges",col:Y2},
            {n:14,label:"Suburban zip codes across Delaware, Bucks, and Montgomery Counties",col:Y3},
          ].map((s,i)=>(
            <div key={i} style={{borderTop:`2px solid ${s.col}`,paddingTop:14}}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:38,color:s.col,lineHeight:1}}>{s.n}</div>
              <div style={{fontFamily:"Georgia,serif",fontSize:12,color:"rgba(255,255,255,0.38)",marginTop:7,lineHeight:1.5}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:6}}>
          {zipRows.map(z=>(
            <div key={z.zip} style={{background:z.tier==="high"?"rgba(252,227,138,0.09)":z.tier==="moderate"?"rgba(252,227,138,0.04)":"rgba(255,255,255,0.025)",border:`1px solid ${z.tier==="high"?"rgba(252,227,138,0.2)":"rgba(255,255,255,0.05)"}`,borderRadius:3,padding:"8px 10px"}}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:12,color:z.tier==="high"?Y1:"rgba(255,255,255,0.55)"}}>{z.zip}</div>
              <div style={{fontFamily:"Lato,sans-serif",fontSize:9,color:"rgba(255,255,255,0.28)",marginTop:2}}>{z.nbhd}</div>
            </div>
          ))}
        </div>
        <p style={{fontFamily:"Lato,sans-serif",fontSize:10,color:"rgba(255,255,255,0.18)",marginTop:12,lineHeight:1.6}}>Food access classification based on USDA FARA 2019. Zip code data from 12-Week Extended Care Program only.</p>
      </Section>

      {/* RETENTION */}
      <Section id="retention" bg={WH}>
        <Chip label="Member Perceptions & Plan Retention"/>
        <H2>The retention signal health plans need to see.</H2>
        <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:15,color:MG,marginBottom:32,maxWidth:540,lineHeight:1.7}}>% of members agreeing with each statement post-program. Oct and Dec cohorts with combined average.</p>
        <div style={{display:"grid",gridTemplateColumns:isTablet?"1fr":"auto 1fr",gap:28,alignItems:"center",background:LG,borderRadius:4,padding:isMobile?"24px 18px":"32px 40px",marginBottom:32,borderLeft:`3px solid ${Y2}`}}>
          <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:isMobile?68:90,color:BK,lineHeight:1}}>
            <AnimNum target={84} suffix="%" dur={1600}/>
          </div>
          <div>
            <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?15:18,color:BK,margin:"0 0 6px",lineHeight:1.5}}>of December cohort members said FareRx makes them more likely to stay with their health plan.</p>
            <p style={{fontFamily:"Lato,sans-serif",fontSize:12,color:MG,margin:0,lineHeight:1.6}}>72% combined across all cohorts · In a competitive MA market with $500–$1,500 member acquisition costs, this is calculable retention ROI.</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={isMobile?180:250}>
          <BarChart data={perceptions} margin={{left:isMobile?0:4,right:12,top:0,bottom:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false}/>
            <XAxis dataKey="label" tick={{fontFamily:"Lato,sans-serif",fontSize:isMobile?9:11,fill:DG}}/>
            <YAxis domain={[0,100]} tick={{fontFamily:"Lato,sans-serif",fontSize:10,fill:MG}} tickFormatter={v=>`${v}%`}/>
            <Tooltip content={<CTip/>}/>
            <Bar dataKey="oct" name="Oct Program" fill="#ddd" radius={[3,3,0,0]} barSize={isMobile?10:15}/>
            <Bar dataKey="dec" name="Dec Program" fill={Y2} radius={[3,3,0,0]} barSize={isMobile?10:15}/>
            <Bar dataKey="combined" name="Combined" fill="#444" radius={[3,3,0,0]} barSize={isMobile?10:15}/>
          </BarChart>
        </ResponsiveContainer>
        <div style={{display:"flex",gap:14,marginTop:8,flexWrap:"wrap"}}>
          {[["#ddd","Oct Program"],[Y2,"Dec Program"],["#444","Combined"]].map(([c,l])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
              <div style={{width:8,height:8,background:c,borderRadius:2}}/>
              <span style={{fontFamily:"Lato,sans-serif",fontSize:11,color:MG}}>{l}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* DELIVERY */}
      <Section bg={LG}>
        <div style={{display:"grid",gridTemplateColumns:isTablet?"1fr":"1fr 1fr",gap:isTablet?28:52,alignItems:"start"}}>
          <div>
            <Chip label="Operational Quality"/>
            <H2 size={28}>Every delivery is a brand touchpoint for your plan.</H2>
            <Body>98–100% of members confirmed their driver was on-time, respectful, and represented the health plan professionally. That's brand equity, not just a logistics stat.</Body>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:14}}>
              {[["4.90/5","Avg Rating"],["98%","On-Time Combined"],["99%","Driver Quality"]].map(([n,l])=>(
                <div key={n} style={{borderTop:`2px solid ${Y2}`,paddingTop:10}}>
                  <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:18,color:BK}}>{n}</div>
                  <div style={{fontFamily:"Lato,sans-serif",fontSize:10,color:MG,marginTop:3}}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={deliveryData} margin={{left:0,right:12,top:8,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false}/>
                <XAxis dataKey="metric" tick={{fontFamily:"Lato,sans-serif",fontSize:isMobile?9:11,fill:DG}}/>
                <YAxis domain={[50,105]} tick={{fontFamily:"Lato,sans-serif",fontSize:10,fill:MG}} tickFormatter={v=>`${v}%`}/>
                <Tooltip content={<CTip/>}/>
                <ReferenceLine y={95} stroke={Y2} strokeDasharray="4 4" label={{value:"95% target",position:"insideTopRight",fill:MG,fontFamily:"Lato,sans-serif",fontSize:9}}/>
                <Bar dataKey="oct" name="Oct Program" fill="#ddd" radius={[3,3,0,0]} barSize={15}/>
                <Bar dataKey="dec" name="Dec Program" fill="#444" radius={[3,3,0,0]} barSize={15}/>
                <Bar dataKey="combined" name="Combined" fill={Y2} radius={[3,3,0,0]} barSize={15}/>
              </BarChart>
            </ResponsiveContainer>
            <div style={{display:"flex",gap:14,marginTop:8,flexWrap:"wrap"}}>
              {[["#ddd","Oct Program"],["#444","Dec Program"],[Y2,"Combined"]].map(([c,l])=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                  <div style={{width:8,height:8,background:c,borderRadius:2}}/>
                  <span style={{fontFamily:"Lato,sans-serif",fontSize:11,color:MG}}>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* MEMBER VOICES */}
      <Section id="voices" bg={WH}>
        <div style={{marginBottom:32}}>
          <Chip label="In Their Own Words"/>
          <H2>What members said.</H2>
          <p style={{fontFamily:"Georgia,serif",fontSize:13,color:MG}}>Recorded by FareRx program navigators during exit interviews. Sept–Dec 2025.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:10}}>
          {[
            "I loved the program so much, I wish it was longer! Every Friday I looked forward to it. Everything was really good, fresh, and packaged well.",
            "It's an excellent program, especially for people like me who don't get out of the house much.",
            "I would like to thank you all for thinking about me and others like me that sometimes don't get a chance to shop. I think the program is an excellent reminder of what we should be eating!",
            "We ate more salad than we ever had before. It was an absolute blessing.",
            "She made sure to write down all the new things she tried so she could keep eating them. She would be very excited to pay for a similar program.",
            "It was one of the only deliveries that ran smoothly for me. An exceptional program with outstanding service.",
          ].map((q,i)=>(
            <div key={i} style={{background:LG,borderRadius:4,padding:"20px 18px",borderLeft:`3px solid ${Y2}`}}>
              <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:15,color:DG,lineHeight:1.75,margin:0,fontStyle:"italic"}}>"{q}"</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FOR PLANS */}
      <Section bg={LG}>
        <div style={{marginBottom:28}}>
          <Chip label="For Health Plan Partners"/>
          <H2>Four questions. Four data-backed answers.</H2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:10,marginBottom:28}}>
          {[
            {q:"Does it affect member retention?",a:"Yes. 72–84% of members said FareRx makes them more likely to stay with their plan. In a competitive MA market with $500–$1,500 member acquisition costs, retention is measurable ROI."},
            {q:"Does it affect the behaviors that drive downstream cost?",a:"Yes. Medication adherence maintained or improved in 89–92% of all members. The 12-week LIS cohort showed a +0.22 gain on a 5-point adherence scale.[3,6]"},
            {q:"Is there a dose-response?",a:"Yes. The 12-week program produced 3.3–3.7× larger behavioral gains than the 4-week program. For LIS-eligible populations, program duration should be a benefit design input, not a budget variable.[4]"},
            {q:"Can it be delivered at scale with operational consistency?",a:"Yes. 4.90/5 average delivery satisfaction. 100% on-time delivery (December cohort). 100% fresh produce confirmation. Consistent service across 24 zip codes and 102 member interactions."},
          ].map((item,i)=>(
            <div key={i} style={{background:WH,borderRadius:4,padding:isMobile?"18px 16px":"24px 22px",borderTop:`2px solid ${Y2}`}}>
              <div style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:9,color:MG,letterSpacing:1.5,marginBottom:7,textTransform:"uppercase"}}>Q{i+1}</div>
              <h3 style={{fontFamily:"Georgia,serif",fontSize:isMobile?15:16,color:BK,margin:"0 0 9px",lineHeight:1.4}}>{item.q}</h3>
              <p style={{fontFamily:"Georgia,serif",fontSize:14,color:DG,lineHeight:1.72,margin:0}}>{item.a}</p>
            </div>
          ))}
        </div>
        <div style={{background:BK,borderRadius:4,padding:isMobile?"26px 20px":"36px 40px",display:"grid",gridTemplateColumns:isTablet?"1fr":"1fr auto",gap:24,alignItems:"center"}}>
          <div>
            <h3 style={{fontFamily:"Georgia,serif",fontSize:isMobile?17:22,color:WH,margin:"0 0 8px"}}>Ready to bring this benefit to your members?</h3>
            <p style={{fontFamily:"Georgia,serif",fontSize:14,color:"rgba(255,255,255,0.48)",margin:0,lineHeight:1.7}}>FareRx is seeking health plan and employer partners to expand Food is Medicine delivery programs and connect grocery delivery to credentialed MNT services and CPT-reimbursable nutrition interventions.</p>
          </div>
          <div style={{flexShrink:0}}>
            <div style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:12,color:Y1,marginBottom:2}}>Mike Cangi, Founder & CEO</div>
            <div style={{fontFamily:"Lato,sans-serif",fontSize:11,color:"rgba(255,255,255,0.28)",marginBottom:14}}>farerx.com</div>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              <a href="mailto:mike@farerx.com" style={{display:"block",background:Y2,color:BK,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,letterSpacing:1.5,padding:"10px 20px",borderRadius:2,textTransform:"uppercase",textDecoration:"none",textAlign:"center"}}>Get in Touch</a>
              <button onClick={()=>setShowModal(true)} style={{background:"transparent",color:"rgba(255,255,255,0.55)",fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,letterSpacing:1.5,padding:"10px 20px",borderRadius:2,textTransform:"uppercase",cursor:"pointer",border:"1px solid rgba(255,255,255,0.12)",textAlign:"center"}}>↓ Download Report</button>
            </div>
          </div>
        </div>
      </Section>

      {/* LITERATURE */}
      <Section id="refs" bg={WH}>
        <Chip label="Supporting Literature"/>
        <H2>FareRx in Context</H2>
        <p style={{fontFamily:"Georgia,serif",fontSize:isMobile?14:15,color:DG,marginBottom:24,maxWidth:700,lineHeight:1.82}}>
          FareRx program-level outcomes are directionally consistent with — and in key metrics exceed — findings from peer-reviewed Food is Medicine research. Berkowitz et al.[3] demonstrated improved glycemic outcomes with MTM delivery for food-insecure diabetic patients. Doyle et al.[4] confirmed improved health and care utilization in a JAMA Internal Medicine RCT. Health Affairs (2025)[5] modeled MTM coverage as net cost-saving in 49 of 50 states.
        </p>
        <div style={{overflowX:"auto",marginBottom:24}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:520}}>
            <thead>
              <tr>
                <th style={{...thStyle,minWidth:160}}>Metric</th>
                <th style={thStyle}>Published Literature</th>
                <th style={{...thStyle,background:Y3,color:DG,borderBottom:`2px solid ${Y2}`}}>FareRx 2025</th>
              </tr>
            </thead>
            <tbody>
              {litRows.map((r,i)=>(
                <tr key={i} style={{background:i%2===0?WH:LG}}>
                  <td style={tdStyle}><strong>{r.metric}</strong></td>
                  <td style={tdStyle}>{r.lit}</td>
                  <td style={tdY}>{r.farerx}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{fontFamily:"Lato,sans-serif",fontSize:11,color:MG,lineHeight:1.65,maxWidth:660,borderLeft:`2px solid ${Y2}`,paddingLeft:10,marginBottom:28}}>
          * FareRx is pursuing claims data linkage and A1c integration through health plan partnerships and Teaching Kitchen MNT credentialing. Clinical endpoint data is a stated priority for 2026 cohorts.
        </p>
        <div style={{display:"grid",gridTemplateColumns:col2,gap:8}}>
          {refs.map(r=>(
            <div key={r.n} style={{display:"flex",gap:12,background:LG,borderRadius:4,padding:"14px 16px",alignItems:"flex-start"}}>
              <div style={{background:Y2,color:BK,fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:10,width:22,height:22,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>{r.n}</div>
              <p style={{fontFamily:"Georgia,serif",fontSize:12,color:DG,lineHeight:1.65,margin:0}}>
                <a href={r.url} target="_blank" rel="noopener noreferrer" style={{color:DG,textDecoration:"none",borderBottom:`1px solid ${Y2}`}}>{r.text}</a>
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* DOWNLOAD BAND */}
      <Section bg={LG} pt="44px" pb="44px">
        <div style={{display:"grid",gridTemplateColumns:isTablet?"1fr":"1fr auto",gap:20,alignItems:"center"}}>
          <div>
            <div style={{fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:10,letterSpacing:2,color:MG,textTransform:"uppercase",marginBottom:7}}>Full Report Available</div>
            <h3 style={{fontFamily:"Georgia,serif",fontSize:isMobile?17:22,color:BK,margin:"0 0 6px",lineHeight:1.3}}>Download the complete 2025 Outcomes Report.</h3>
            <p style={{fontFamily:"Georgia,serif",fontSize:14,color:MG,margin:0,lineHeight:1.7}}>11-page PDF with full data tables, program methodology, literature comparison, and implications for health plan partners.</p>
          </div>
          <button onClick={()=>setShowModal(true)} style={{background:BK,color:Y1,fontFamily:"Lato,sans-serif",fontWeight:700,fontSize:11,letterSpacing:1.5,padding:"13px 26px",borderRadius:2,border:"none",cursor:"pointer",textTransform:"uppercase",whiteSpace:"nowrap",flexShrink:0}}>↓ Get the PDF</button>
        </div>
      </Section>

      {/* FOOTER */}
      <div style={{background:BK,padding:isMobile?"16px 24px":"18px 36px"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"flex",flexDirection:isMobile?"column":"row",alignItems:isMobile?"flex-start":"center",justifyContent:"space-between",gap:6}}>
          <div style={{fontFamily:"Lato,sans-serif",fontWeight:900,fontSize:13,color:Y1,letterSpacing:2}}>FARERX</div>
          <div style={{fontFamily:"Lato,sans-serif",fontSize:10,color:"rgba(255,255,255,0.18)",letterSpacing:0.5}}>FareRx Member Hospitality Group · Sept–Dec 2025 · Philadelphia, PA · farerx.com</div>
        </div>
      </div>
    </div>
  );
}
